import { useCallback, useEffect, useRef, useState } from "react";
import { useSocket } from "../../../context/SocketContext";
import { createDeviceTracker, deleteDeviceTracker } from "../../../utils";
import { readCreatedDevice } from "../devicePayload";
import { useAppSendInvitationMutation } from "./useAppSendInvitationMutation";

export const useDevice = ({ workforceUid, deviceTrackerUid, device, setDevice, token, companyId, getDeviceById }) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [deleted, setDeleted] = useState(false);
  const [deviceLoaded, setDeviceLoaded] = useState(false);
  const [isQRHovered, setIsQRHovered] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const qrCodeRef = useRef(null);

  const {
    mutate: sendInvitation,
    isPending: sendingInvitation,
    isSuccess: invitationSuccess,
    isError: invitationError,
    error,
  } = useAppSendInvitationMutation();

  const { socket } = useSocket() || {};

  // Mark device loaded
  useEffect(() => {
    if (device !== undefined) {
      setDeviceLoaded(true);
    }
  }, [device]);

  useEffect(() => {
    if (!socket || !workforceUid || !companyId) return;

    const room = `room_company_${companyId}_workforce_${workforceUid}`;
    const join = () => socket.emit("join", [room]);
    if (socket.connected) join();
    socket.on("connect", join);

    const handleLinkedDevice = (data: any) => {
      try {
        const parsed = typeof data === "string" ? JSON.parse(data) : data;

        if (parsed?.workforceUid && parsed.workforceUid !== workforceUid) return;

        setDevice((current) => ({
          ...(current && typeof current === "object" ? current : {}),
          ...parsed,
          status: "linked",
          uid: parsed.deviceUid || parsed.uid || current?.uid,
        }));

        setDeviceLoaded(true);
      } catch (e) {
        console.error("Error parsing device link:", e);
      }
    };

    socket.on("deviceTracker:link", handleLinkedDevice);

    return () => {
      socket.off("connect", join);
      socket.off("deviceTracker:link", handleLinkedDevice);
    };
  }, [socket, workforceUid, companyId, setDevice]);

  const handleCreate = useCallback(async () => {
    try {
      setLoading(true);
      setDeleted(false);
      const res = await createDeviceTracker(token, { workforceUid });
      const created = readCreatedDevice(res);
      if (!created?.shortUid) {
        setResult({ error: "Pairing code was not returned. Try again." });
        return;
      }
      setDevice(created);
      setDeviceLoaded(true);
      setResult(null);
      if (getDeviceById) {
        await getDeviceById(workforceUid, true);
      }
    } catch (err) {
      setResult({ error: err.message || "Error creating device tracker" });
    } finally {
      setLoading(false);
    }
  }, [token, workforceUid, setDevice, getDeviceById]);

  // Delete tracker
  const handleDelete = useCallback(async () => {
    if (!window.confirm("Delete this Device Tracker?")) return;
    try {
      setLoading(true);
      await deleteDeviceTracker(token, deviceTrackerUid);
      setDeleted(true);
    } catch (err) {
      setResult({ error: err.message || "Error deleting tracker" });
    } finally {
      setLoading(false);
    }
  }, [token, deviceTrackerUid]);

  const handleDownloadQR = useCallback(() => {
    const container = qrCodeRef.current;
    if (!container) return;

    const svg = container.querySelector("svg");
    if (!svg) return;

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const svgData = new XMLSerializer().serializeToString(svg);
    const svgBlob = new Blob([svgData], { type: "image/svg+xml" });
    const url = URL.createObjectURL(svgBlob);

    const img = new Image();
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;

      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);

      canvas.toBlob((blob) => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "qr-code.png";
        link.click();
      });
    };

    img.src = url;
  }, []);

  const handleSendDownloadEmail = useCallback(
    (email, companyName, targetName) => {
      sendInvitation(
        { email, companyName, targetName },
        {
          onSuccess: () => {
            setEmailSent(true);
            setResult({ success: "Invitation email sent successfully!" });

            setTimeout(() => {
              setEmailSent(false);
              setResult(null);
            }, 4500);
          },

          onError: (err) => {
            setResult({ error: err.message || "Email send failed" });
            console.error("Invitation send error:", err);
          },
        }
      );
    },
    [sendInvitation]
  );

  return {
    loading,
    result,
    deleted,
    deviceLoaded,
    emailSent,
    isQRHovered,
    setIsQRHovered,
    sendingInvitation,
    invitationSuccess,
    invitationError,
    qrCodeRef,

    handleCreate,
    handleDelete,
    handleDownloadQR,
    handleSendDownloadEmail,
  };
};
